import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpenCheck, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Logo, BRAND_NAME } from "../components/ui/Logo";
import { RESOURCE_ARTICLES } from "../lib/resourcesData";

const FEATURED_SLUG = "automatizar-vgi-consulta-medica";

const APP_LOGIN_URL = process.env.REACT_APP_APP_URL || "https://app.aurorageriatrica.com/login";
const getTrialUrl = () =>
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
    ? "/login"
    : APP_LOGIN_URL;

export default function ResourcesIndex() {
  const { featuredArticle, otherArticles } = useMemo(() => {
    const featured = RESOURCE_ARTICLES.find((a) => a.slug === FEATURED_SLUG);
    const others = RESOURCE_ARTICLES.filter((a) => a.slug !== FEATURED_SLUG);
    return { featuredArticle: featured, otherArticles: others };
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-800">
      <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/95 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex items-center gap-2 font-semibold text-slate-900">
            <Logo variant="icon" size="md" className="h-8 w-8" alt="" />
            {BRAND_NAME}
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/" className="text-sm text-slate-600 hover:text-slate-900">
              Inicio
            </Link>
            <a href={getTrialUrl()}>
              <Button size="sm" className="bg-sky-600 hover:bg-sky-700 text-white">
                Probar gratis
              </Button>
            </a>
          </div>
        </div>
      </header>

      <main>
        <section className="border-b border-slate-100 bg-gradient-to-b from-sky-50/40 via-white to-slate-50/60 px-4 py-14 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-sky-700">
              <BookOpenCheck className="h-3.5 w-3.5" />
              Recursos
            </div>
            <h1 className="mt-5 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
              Mejore su consulta geriatrica en minutos
            </h1>
            <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
              Organice su expediente geriátrico, agenda clínica e informes profesionales para consulta y seguimiento.
            </p>
            <ul className="mx-auto mt-6 max-w-md space-y-2.5 text-left text-sm text-slate-700 sm:max-w-lg">
              {[
                "Expediente geriátrico organizado",
                "Agenda clínica y seguimiento",
                "Informes profesionales",
              ].map((line) => (
                <li key={line} className="flex items-start gap-2.5">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center">
              <a href={getTrialUrl()} className="sm:inline-flex">
                <Button className="w-full bg-sky-600 text-white hover:bg-sky-700 sm:w-auto">Probar gratis</Button>
              </a>
              <Link to="/demo-clinico" className="sm:inline-flex">
                <Button variant="outline" className="w-full border-slate-300 sm:w-auto">
                  Ver demostracion
                  <ArrowRight className="ml-1.5 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        <section className="border-b border-slate-100 bg-slate-50/40 px-4 py-6 text-center sm:px-6 lg:px-8">
          <p className="mx-auto max-w-3xl text-sm leading-relaxed text-slate-600 sm:text-base">
            Utilizado por medicos en Latinoamerica para optimizar la evaluacion geriatrica
          </p>
        </section>

        <section className="px-4 py-14 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-6xl space-y-6">
            {featuredArticle ? (
              <Card className="overflow-hidden border-2 border-sky-200/90 bg-gradient-to-br from-sky-50/80 via-white to-white shadow-md ring-1 ring-sky-100/80">
                <CardContent className="flex flex-col gap-4 p-6 sm:flex-row sm:items-start sm:justify-between sm:gap-8 sm:p-8">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge className="border-0 bg-sky-600 text-white hover:bg-sky-600">Recomendado</Badge>
                      <Badge variant="outline" className="border-slate-200 bg-white text-slate-600">
                        {featuredArticle.category}
                      </Badge>
                      <span className="text-xs text-slate-500">{featuredArticle.readTime}</span>
                    </div>
                    <h2 className="mt-4 text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl">
                      {featuredArticle.title}
                    </h2>
                    <p className="mt-3 text-sm leading-relaxed text-slate-600 sm:text-base">{featuredArticle.summary}</p>
                  </div>
                  <div className="flex shrink-0 flex-col justify-end sm:pt-1">
                    <Link
                      to={`/recursos/${featuredArticle.slug}`}
                      className="inline-flex items-center text-sm font-semibold text-sky-700 hover:text-sky-800 sm:text-base"
                    >
                      Leer guia completa &rarr;
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ) : null}

            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {otherArticles.map((article) => (
                <Card key={article.slug} className="h-full border-slate-200/80 shadow-sm transition-shadow hover:shadow-md">
                  <CardContent className="flex h-full flex-col p-6">
                    <div className="flex items-center justify-between gap-2">
                      <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-600">
                        {article.category}
                      </Badge>
                      <span className="text-xs text-slate-500">{article.readTime}</span>
                    </div>
                    <h2 className="mt-4 text-lg font-semibold tracking-tight text-slate-900">{article.title}</h2>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-600">{article.summary}</p>
                    <Link
                      to={`/recursos/${article.slug}`}
                      className="mt-5 inline-flex items-center text-sm font-semibold text-sky-700 hover:text-sky-800"
                    >
                      Leer guia completa &rarr;
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
